'use client';

import { useTranslations } from 'next-intl';
import { SectionWrapper } from '@/components/ui/section-wrapper';
import { Typography } from '@/components/ui/typography';
import { LucideIcon } from 'lucide-react';
import { useScrollAnimation } from '@/hooks/use-scroll-animation';
import { cn } from '@/lib/utils';

interface Plan {
    name: string;
}

interface CommissionConfig {
    icon: LucideIcon;
    color: 'primary' | 'blue' | 'green' | 'purple' | 'orange' | 'pink';
    titleKey: string;
    descriptionKey: string;
}

interface CommissionsSectionProps {
    plan: Plan;
    translationNamespace: string;
    commissions: CommissionConfig[];
    gridCols?: '2' | '3' | '4';
    hasDescription?: boolean;
}

export function CommissionsSection({ 
    plan, 
    translationNamespace, 
    commissions,
    gridCols = '3',
    hasDescription = false
}: CommissionsSectionProps) {
    const t = useTranslations(translationNamespace);
    const sectionRef = useScrollAnimation({ threshold: 0.15 });

    const getColorClasses = (color: string) => {
        const colors: Record<string, { border: string; hover: string; iconBg: string }> = {
            primary: {
                border: 'border-primary-200 dark:border-primary-800 hover:border-primary-400 dark:hover:border-primary-600',
                hover: 'hover:border-primary-400 dark:hover:border-primary-600',
                iconBg: 'bg-primary-100 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400',
            },
            blue: {
                border: 'border-blue-200 dark:border-blue-800 hover:border-blue-400 dark:hover:border-blue-600',
                hover: 'hover:border-blue-400 dark:hover:border-blue-600',
                iconBg: 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400',
            },
            green: {
                border: 'border-green-200 dark:border-green-800 hover:border-green-400 dark:hover:border-green-600',
                hover: 'hover:border-green-400 dark:hover:border-green-600',
                iconBg: 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400',
            },
            purple: {
                border: 'border-purple-200 dark:border-purple-800 hover:border-purple-400 dark:hover:border-purple-600',
                hover: 'hover:border-purple-400 dark:hover:border-purple-600',
                iconBg: 'bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400',
            },
            orange: {
                border: 'border-orange-200 dark:border-orange-800 hover:border-orange-400 dark:hover:border-orange-600',
                hover: 'hover:border-orange-400 dark:hover:border-orange-600',
                iconBg: 'bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400',
            },
            pink: {
                border: 'border-pink-200 dark:border-pink-800 hover:border-pink-400 dark:hover:border-pink-600',
                hover: 'hover:border-pink-400 dark:hover:border-pink-600',
                iconBg: 'bg-pink-100 dark:bg-pink-900/30 text-pink-600 dark:text-pink-400',
            },
        };
        return colors[color] || colors.primary;
    };

    const gridClass = {
        '2': 'md:grid-cols-2',
        '3': 'md:grid-cols-2 lg:grid-cols-3',
        '4': 'md:grid-cols-2 lg:grid-cols-4',
    }[gridCols] || 'md:grid-cols-2 lg:grid-cols-3';

    return (
        <SectionWrapper ref={sectionRef.ref} variant="default" padding="lg" className="relative bg-gray-50 dark:bg-gray-900/50">
            <div className="container">
                <div className={cn(
                    "mb-12 text-center transition-all duration-700",
                    sectionRef.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
                )}>
                    <Typography variant="h2" className="mb-4">
                        {t('title')}
                    </Typography>
                    {hasDescription && (
                        <Typography variant="p" className="text-gray-600 dark:text-gray-400 max-w-4xl mx-auto">
                            {t('description')}
                        </Typography>
                    )}
                </div>

                <div className={`grid ${gridClass} gap-6 max-w-7xl mx-auto`}>
                    {commissions.map((commission, index) => {
                        const Icon = commission.icon;
                        const colors = getColorClasses(commission.color);
                        return (
                            <div
                                key={index}
                                className={cn(
                                    `bg-white dark:bg-gray-800 rounded-2xl p-6 border-2 ${colors.border} transition-all duration-700 hover:shadow-xl flex flex-col items-center text-center`,
                                    sectionRef.isVisible ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-10 scale-95'
                                )}
                                style={{ transitionDelay: `${200 + index * 100}ms` }}
                            >
                                <div className={`w-16 h-16 rounded-full ${colors.iconBg} flex items-center justify-center mb-4`}>
                                    <Icon className="w-8 h-8" />
                                </div>
                                <Typography variant="h3" className="text-lg font-bold text-gray-900 dark:text-white mb-3">
                                    {t(commission.titleKey)}
                                </Typography>
                                <Typography variant="p" className="text-gray-700 dark:text-gray-300 leading-relaxed text-center">
                                    {t(commission.descriptionKey)}
                                </Typography>
                            </div>
                        );
                    })}
                </div>
            </div>
        </SectionWrapper>
    );
}

